import { type AuditText, auditMessage } from './text';
import type { AuditLogEntry, AuditLogFilters } from './types';
import { AUDIT_ACTOR_TYPES } from './types';

/** An email renders as two parts so the domain can truncate first and stay muted. */
export type AuditActorName =
    | { kind: 'email'; localPart: string; domain: string }
    | { kind: 'text'; text: AuditText };

export interface AuditActorDescription {
    name: AuditActorName;
    /** The whole name as one text, for the cell's title and chips. */
    text: AuditText;
    /** What clicking the actor filters by, or null when nothing can match it exactly. */
    filter: AuditLogFilters | null;
    /** "Impersonated by ...", present only when an admin acted as this user. */
    impersonator: { text: AuditText; filter: AuditLogFilters } | null;
}

function emailName(email: string): AuditActorName {
    const at = email.lastIndexOf('@');
    return at <= 0
        ? { kind: 'text', text: email }
        : { kind: 'email', localPart: email.slice(0, at), domain: email.slice(at) };
}

function typeText(actorType: string): AuditText {
    return auditMessage(`auditLog.actorType.${actorType}`, undefined, actorType);
}

function actorText(entry: AuditLogEntry): AuditText {
    const id = entry.actorUserId;
    if (entry.actorType === 'user') {
        if (entry.actorEmail !== null) return entry.actorEmail;
        return id === null
            ? typeText('user')
            : auditMessage('auditLog.actor.userId', { id: String(id) });
    }
    if (entry.actorType === 'api_user' && id !== null) {
        return auditMessage('auditLog.actor.apiUserId', { id: String(id) });
    }
    return typeText(entry.actorType);
}

/**
 * Users and API users share ids, so an id filter always carries its type. An actor type
 * the page does not know cannot be sent, so it has no filter.
 */
function actorFilter(entry: AuditLogEntry): AuditLogFilters | null {
    const actorType = AUDIT_ACTOR_TYPES.find((known) => known === entry.actorType);
    if (actorType === undefined) return null;
    if (entry.actorUserId !== null) {
        return { actorType: [actorType], actorUserId: entry.actorUserId };
    }
    if (entry.actorEmail !== null) return { actorEmail: entry.actorEmail };
    return { actorType: [actorType] };
}

function impersonator(entry: AuditLogEntry): AuditActorDescription['impersonator'] {
    const { impersonatorEmail: email, impersonatorUserId: id } = entry;
    if (email === null && id === null) return null;
    const admin: AuditText =
        email ?? auditMessage('auditLog.actor.impersonatorId', { id: String(id) });
    return {
        text: auditMessage('auditLog.actor.impersonatedBy', { admin }),
        filter:
            id === null ? { impersonatorEmail: email ?? undefined } : { impersonatorUserId: id },
    };
}

/** Who the request acted as, and who, if anyone, was acting as them. */
export function describeActor(entry: AuditLogEntry): AuditActorDescription {
    const text = actorText(entry);
    return {
        name:
            typeof text === 'string' && entry.actorEmail === text
                ? emailName(text)
                : { kind: 'text', text },
        text,
        filter: actorFilter(entry),
        impersonator: impersonator(entry),
    };
}
